import React, { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import DrillForm from './DrillForm';
import ConfirmModal from '../ConfirmModal';
import { DrillCategory } from '../../types/DrillCategory';
import { Drill } from '../../types/Drill';

const DrillManager: React.FC = () => {
  const { user } = useAuthContext();
  const [categories, setCategories] = useState<DrillCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(8);

  const [drills, setDrills] = useState<Drill[]>([]);
  const [selectedDrill, setSelectedDrill] = useState<Drill | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState<Drill | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Drill | null>(null);

  const fetchDrills = async (categoryId = selectedCategoryId) => {
    if (!user?.teamId || categoryId === null) return;

    const defaultQ = query(
      collection(db, 'drills'),
      where('categoryId', '==', categoryId),
      where('isDefault', '==', true)
    );

    const customQ = query(
      collection(db, `teams/${user.teamId}/drills`),
      where('categoryId', '==', categoryId)
    );

    const [defaultSnap, customSnap] = await Promise.all([
      getDocs(defaultQ),
      getDocs(customQ),
    ]);

    const defaultDrills = defaultSnap.docs.map(doc => ({
      ...(doc.data() as Drill),
      id: doc.id,
      isDefault: true,
    }));

    const customDrills = customSnap.docs.map(doc => ({
      ...(doc.data() as Drill),
      id: doc.id,
      isDefault: false,
    }));

    setDrills(
      [...defaultDrills, ...customDrills].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    );
    setSelectedDrill(null);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, 'drillCategories'));
      const items = snap.docs.map(doc => ({
        id: parseInt(doc.id),
        ...(doc.data() as Omit<DrillCategory, 'id'>),
      }));
      setCategories(items.sort((a, b) => a.name.localeCompare(b.name)));
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDrills();
  }, [selectedCategoryId, user?.teamId]);

  const handleDelete = async (drill: Drill) => {
    if (!user?.teamId) return;
    await deleteDoc(doc(db, `teams/${user.teamId}/drills`, drill.id));
    setConfirmDelete(null);
    fetchDrills();
  };

  return (
    <div className='p-4'>
      <h2 className='text-2xl font-bold mb-4'>Training Drills</h2>

      <div className='flex justify-end mb-4'>
        <button
          className='bg-green-600 text-white px-4 py-2 rounded'
          onClick={() => {
            setEditingDrill(null);
            setShowForm(true);
          }}
        >
          + Add Drill
        </button>
      </div>

      {showForm && (
        <DrillForm
          existingDrill={editingDrill}
          onClose={() => {
            setShowForm(false);
            setEditingDrill(null);
          }}
          onSaved={categoryId => {
            setSelectedCategoryId(categoryId);
            fetchDrills(categoryId);
            setShowForm(false);
            setEditingDrill(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title='Delete Drill'
          message={`Are you sure you want to delete \"${confirmDelete.name}\"?`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete)}
        />
      )}

      <div className='mb-4 space-x-2 overflow-x-auto whitespace-nowrap'>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`px-4 py-2 rounded-full text-sm ${
              selectedCategoryId === cat.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
            onClick={() => setSelectedCategoryId(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <ul className='divide-y border rounded bg-white'>
        {drills.map(drill => (
          <li key={drill.id} className='p-3 cursor-pointer hover:bg-gray-50'>
            <div
              className='flex justify-between items-center'
              onClick={() =>
                setSelectedDrill(prev => (prev?.id === drill.id ? null : drill))
              }
            >
              <span className='font-bold'>
                {drill.name} ({drill.duration} min)
              </span>
              <div className='flex gap-2 items-center'>
                {drill.isDefault ? (
                  <span className='text-xs bg-darkblue text-lightblue px-2 py-1 rounded-full font-bold'>
                    PITCH<span className='text-white'>SIDE</span>
                  </span>
                ) : (
                  <>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setEditingDrill(drill);
                        setShowForm(true);
                      }}
                      className='bg-darkblue text-white px-3 py-1 rounded text-sm'
                    >
                      Edit
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setConfirmDelete(drill);
                      }}
                      className='bg-red-600 text-white px-3 py-1 rounded text-sm'
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
            {selectedDrill?.id === drill.id && (
              <div className='mt-2'>
                <p className='text-sm'>{drill.description}</p>
                <ul className='list-disc list-inside text-sm'>
                  {drill.equipment.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DrillManager;
